import React, { useMemo } from "react"
import { LinePath } from "@visx/shape"
import { scaleLinear } from "@visx/scale"
import { curveMonotoneX } from "@visx/curve"
import { ParentSize } from "@visx/responsive"
import type { TimeSeriesDataPoint } from "../types"
import { CHART_COLORS } from "../theme"
import { getTimeX, getTimeY, getExtent } from "../utils/dataUtils"

const strokeWidth = 2

type SparklineProps = {
    width: number
    height: number
    data: TimeSeriesDataPoint[]
}

const InnerSparkline = ({ width, height, data }: SparklineProps) => {
    if (width < 10 || height < 10 || data.length === 0) return null

    const xScale = useMemo(() => {
        const [min, max] = getExtent(data, getTimeX)
        return scaleLinear({ range: [0, width], domain: [min, max] })
    }, [width, data])

    const yScale = useMemo(() => {
        const [min, max] = getExtent(data, getTimeY)
        const padding = (max - min) * 0.1 || 1
        return scaleLinear({
            range: [height - strokeWidth, strokeWidth],
            domain: [min - padding, max + padding],
        })
    }, [height, data])

    return (
        <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
            <svg
                width={width}
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="none"
            >
                <g
                    key={data[0]?.x}
                    className="motion-safe:animate-bar-grow origin-bottom [transform-box:fill-box]"
                >
                    <LinePath<TimeSeriesDataPoint>
                        curve={curveMonotoneX}
                        data={data}
                        x={(d) => xScale(getTimeX(d))}
                        y={(d) => yScale(getTimeY(d))}
                        stroke={CHART_COLORS.label}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </g>
            </svg>
        </div>
    )
}

export const PilotTrackingCardChart = ({ data }: { data: TimeSeriesDataPoint[] }) => (
    <ParentSize style={{ width: "100%", height: "100%" }}>
        {({ width, height }) => <InnerSparkline data={data} width={width} height={height} />}
    </ParentSize>
)
